import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { assign } from "lodash";
import { take } from "rxjs/operators";
import { ActivityItemDefinition, FormItemDefinition, ProtocolDefinition } from "@vhealth/engage";
import { ProtocolFacade } from "../store/facade";
import { combineLatest } from "rxjs";
import RRule from "rrule";
import { RecurrenceHelper } from "@vui";

export interface CreateState {
    activityItems: ActivityItemDefinition[];
    formItems: FormItemDefinition[];
    editing: boolean;
    editingItem: ActivityItemDefinition | undefined;
    editingForm: FormItemDefinition | undefined;
}

@Injectable()
export class CreateProtocolComponentStore extends ComponentStore<CreateState> {
    currentId: number = 1;

    constructor(private _facade: ProtocolFacade) {
        super({
            activityItems: [],
            formItems: [],
            editing: false,
            editingItem: undefined,
            editingForm: undefined,
        });
    }

    readonly formItems$ = this.select((state) => state.formItems);

    readonly activityItems$ = this.select((state) => state.activityItems);

    readonly editing$ = this.select((state) => state.editing);

    readonly editingItem$ = this.select((state) => state.editingItem);

    readonly editingForm$ = this.select((state) => state.editingForm);

    readonly isValid$ = this.select(
        (state) =>
            state.formItems.filter((l) => l.id > 0).length > 0 || state.activityItems.filter((l) => l.id > 0).length > 0
    );

    addForm() {
        let activity = new FormItemDefinition();
        this.setState((state) => {
            return {
                ...state,
                formItems: [...state.formItems, activity],
                editingForm: assign(new FormItemDefinition(), activity),
                editing: true,
            };
        });
    }

    addActivity() {
        let activity = new ActivityItemDefinition();
        this.setState((state) => {
            return {
                ...state,
                activityItems: [...state.activityItems, activity],
                editingItem: activity,
                editing: true,
            };
        });
    }

    startActivityEdition(activity: ActivityItemDefinition) {
        this.editingItem$.pipe(take(1)).subscribe((editingProtocol) => {
            if (editingProtocol) {
                this.cancelItemEdition(activity, editingProtocol);
            } else {
                this.setState((state) => {
                    return {
                        ...state,
                        editingItem: activity,
                        editing: true,
                    };
                });
            }
        });
    }

    startFormEdition(activity: FormItemDefinition) {
        this.editingForm$.pipe(take(1)).subscribe((editingProtocol) => {
            if (editingProtocol) {
                this.cancelFormEdition(activity, editingProtocol);
            } else {
                this.setState((state) => {
                    return {
                        ...state,
                        editingForm: activity,
                        editing: true,
                    };
                });
            }
        });
    }

    acceptFormChanges(activity: FormItemDefinition) {
        this.formItems$.pipe(take(1)).subscribe((activities) => {
            let index = activities.findIndex((i) => i.id === activity.id);

            let updatedActivity = assign(new FormItemDefinition(), activity);

            if (updatedActivity.id === 0 || !updatedActivity.id) {
                updatedActivity.id = this.currentId++;
            }

            if (index >= 0) {
                this.currentId++;
                activities.splice(index, 1, updatedActivity);
                this.setState((state) => {
                    return {
                        ...state,
                        formItems: [
                            ...activities.sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1)),
                        ],
                        editing: false,
                        editingForm: undefined,
                    };
                });
            } else {
                this.currentId++;
                this.setState((state) => {
                    activities.splice(activities.length - 1, 1, updatedActivity);
                    return {
                        ...state,
                        formItems: [
                            ...activities.sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1)),
                        ],
                        editing: false,
                        editingForm: undefined,
                    };
                });
            }
        });
    }

    acceptActivityChanges(activity: ActivityItemDefinition) {
        this.activityItems$.pipe(take(1)).subscribe((activities) => {
            let index = activities.findIndex((i) => i.id === activity.id);
            let updatedActivity = assign(new ActivityItemDefinition(), activity);

            if (updatedActivity.id === 0 || !updatedActivity.id) {
                updatedActivity.id = this.currentId++;
            }

            if (index >= 0) {
                this.currentId++;
                activities.splice(index, 1, updatedActivity);
                this.setState((state) => {
                    return {
                        ...state,
                        activityItems: [
                            ...activities.sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1)),
                        ],
                        editing: false,
                        editingItem: undefined,
                    };
                });
            } else {
                this.currentId++;
                this.setState((state) => {
                    activities.splice(activities.length - 1, 1, updatedActivity);
                    return {
                        ...state,
                        activityItems: [
                            ...activities.sort((a, b) => (a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1)),
                        ],
                        editing: false,
                        editingItem: undefined,
                    };
                });
            }
        });
    }

    cancelFormEdition(activity: FormItemDefinition, selectForEdition: FormItemDefinition | undefined = undefined) {
        this.formItems$.pipe(take(1)).subscribe((activities) => {
            let index = activities.findIndex((i) => i.id === activity.id);
            if (index >= 0) {
                if (activity.id === 0) {
                    activities.splice(index, 1);
                }
                this.setState((state) => {
                    return {
                        ...state,
                        formItems: [...activities],
                        editing: !!selectForEdition,
                        editingForm: selectForEdition,
                    };
                });
            }
        });
    }

    cancelItemEdition(
        activity: ActivityItemDefinition,
        selectForEdition: ActivityItemDefinition | undefined = undefined
    ) {
        this.activityItems$.pipe(take(1)).subscribe((activities) => {
            let index = activities.findIndex((i) => i.id === activity.id);
            if (index >= 0) {
                if (activity.id === 0) {
                    activities.splice(index, 1);
                }
                this.setState((state) => {
                    return {
                        ...state,
                        activityItems: [...activities],
                        editing: !!selectForEdition,
                        editingItem: selectForEdition,
                    };
                });
            }
        });
    }

    deleteForm(protocol: FormItemDefinition) {
        this.formItems$.pipe(take(1)).subscribe((activities) => {
            let index = activities.findIndex((i) => i.id === protocol.id);
            if (index >= 0) {
                activities.splice(index, 1);
                this.currentId--;
                this.setState((state) => {
                    return {
                        ...state,
                        formItems: [...activities],
                        editing: false,
                        editingForm: undefined,
                    };
                });
            }
        });
    }

    deleteActivity(protocol: ActivityItemDefinition) {
        this.activityItems$.pipe(take(1)).subscribe((activities) => {
            let index = activities.findIndex((i) => i.id === protocol.id);
            if (index >= 0) {
                activities.splice(index, 1);
                this.currentId--;
                this.setState((state) => {
                    return {
                        ...state,
                        activityItems: [...activities],
                        editing: false,
                        editingItem: undefined,
                    };
                });
            }
        });
    }

    save(data: any) {
        this.setState((state) => ({
            ...state,
            adding: true,
        }));
        combineLatest([this.activityItems$, this.formItems$])
            .pipe(take(1))
            .subscribe(([activities, forms]) => {
                let template = assign(new ProtocolDefinition(), {
                    ...data,
                    items: [
                        ...activities.map((a) => {
                            let activityRecurence = RRule.fromString(a.recurrence);

                            let newRule = RecurrenceHelper.checkForLocalOffsetRemoval(activityRecurence);

                            return assign(a, {
                                ...a,
                                comments: a.comments ?? "",
                                recurrence: RRule.optionsToString(newRule.options),
                            });
                        }),
                        ...forms.map((f) => {
                            let formRecurence = RRule.fromString(f.recurrence);
                            
                            let newRule = RecurrenceHelper.checkForLocalOffsetRemoval(formRecurence);

                            return assign(f, {
                                ...f,
                                recurrence: RRule.optionsToString(newRule.options),
                            });
                        }),
                    ],
                });
                this._facade.create(template);
            });
    }
}
